import './styles.css';
import './lib/i18n';

import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { initializeAuth, UnauthenticatedError } from '@/lib/auth';
import { USE_MOCK_API } from '@/lib/env';
import { deLocalizeUrl, localizeUrl } from '@/lib/localized-routes';
import { queryClient } from '@/lib/query-client';
import { applyDeviceDensity } from '@/lib/screen-utils';
import { labSettingsQuery } from '@/modules/lab-shared/hooks/lab-settings-queries';
import { routeTree } from '@/routeTree.gen';

const router = createRouter({
  routeTree,
  rewrite: {
    input: ({ url }) => deLocalizeUrl(url),
    output: ({ url }) => localizeUrl(url),
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function getContainer(): HTMLElement {
  const container = document.getElementById('root');
  if (!container) throw new Error('Elemento #root não encontrado no documento.');

  return container;
}

function prefetchSessionData() {
  void queryClient.prefetchQuery(labSettingsQuery());
}

function render() {
  prefetchSessionData();
  createRoot(getContainer()).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}

async function startMockApi() {
  const { installMockApi } = await import('@/dev/mock-api');
  installMockApi();
}

async function bootstrap() {
  applyDeviceDensity();

  if (USE_MOCK_API) {
    await startMockApi();
    render();

    return;
  }

  try {
    await initializeAuth();
  } catch (error) {
    if (error instanceof UnauthenticatedError) return;
    throw error;
  }

  render();
}

void bootstrap();
